import { useEffect, useId, useMemo, useRef, type ReactElement } from 'react';
import { History } from 'lucide-react';
import { Modal, ModalBody, ModalHeader } from '@vd/ui';
import type { Ticket } from '@vd/types';
import { TICKET_SCREEN } from '../../lib/screenNames';
import { DevToolsView, devToolsTabFrom, devToolsTabsFor, type TabKey } from './DevToolsPanel';
import { CopyReproduction, SessionView } from './SessionWindow';
import styles from './DebugWindow.module.css';

/** What the debugging window shows: the visit, or one of DevTools' tabs. */
export type DebugTab = 'session' | TabKey;

/** A tab named in the address (`?devtools=session`, `?devtools=errors`), or null for anything else. */
export function debugTabFrom(value: string | null): DebugTab | null {
  return value === 'session' ? 'session' : devToolsTabFrom(value);
}

/** The height of the window's body, the same on every tab so switching does not jump. */
const BODY_HEIGHT = '62vh';

/**
 * A ticket's debugging context in one window (Jason, 24 Oct): Session,
 * Console, Network, Errors and Performance as one row of tabs in the window's
 * header, beside its title, with Copy reproduction at the row's end. A Session
 * row that points at the console, a request or an error opens that tab here.
 *
 * The tab is the caller's, so a page can open it on the tab its address names.
 */
export function DebugWindow({ ticket, open, onClose, tab, onTab, state, teamReporter }: {
  ticket: Ticket | undefined;
  open: boolean;
  onClose: () => void;
  tab: DebugTab;
  onTab: (tab: DebugTab) => void;
  /** While the ticket is still loading, or could not be. */
  state?: 'loading' | 'error';
  /** The teammate who filed it, for the Session tab's "Reported by" (`teamReporterName`). */
  teamReporter?: string | null;
}) {
  const titleId = useId();
  const devTools = useMemo(() => (ticket ? devToolsTabsFor(ticket) : []), [ticket]);
  // Said as it opens, however it was opened, for the board tour's Session step
  // (TOADO-988): on the change to open only, never on a remount already open.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) window.dispatchEvent(new CustomEvent('tour:debug-opened'));
    wasOpen.current = open;
  }, [open]);
  const tabs: Array<{ key: DebugTab; label: string; count?: number; icon: ReactElement }> = [
    { key: 'session', label: 'Session', icon: <History size={12} aria-hidden="true" /> },
    ...devTools,
  ];
  const ready = !state && ticket;
  return (
    // Named by its title alone, not by every tab in the header with it.
    <Modal open={open} onClose={onClose} width={1080} labelledBy={titleId}>
      <ModalHeader className={styles.header}>
        <span id={titleId} className={styles.title}>Debugging context</span>
        {ready && (
          <div className={styles.tabs} role="tablist" aria-label="Debugging context">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                data-testid={`debug-tab-${t.key}`}
                data-tour-anchor={`debug.${t.key}`}
                className={tab === t.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => onTab(t.key)}
              >
                {t.icon}
                <span>{t.label}</span>
                {t.count !== undefined && <span className={styles.count}>{t.count}</span>}
              </button>
            ))}
          </div>
        )}
        {ready && <div className={styles.actions}><CopyReproduction ticket={ticket} /></div>}
      </ModalHeader>
      {state === 'loading' && <ModalBody><p className={styles.state} role="status" data-testid="card-session-loading">Loading the session…</p></ModalBody>}
      {state === 'error' && <ModalBody><p className={styles.state} role="alert" data-testid="card-session-error">The session could not be loaded. Close this and try again.</p></ModalBody>}
      {ready && open && (tab !== 'session'
        ? <DevToolsView ticket={ticket} tab={tab} bodyHeight={BODY_HEIGHT} />
        : (
          <ModalBody style={{ flex: `0 0 ${BODY_HEIGHT}`, height: BODY_HEIGHT, overflowY: 'auto' }} data-toado-view={`${TICKET_SCREEN}/session`}>
            <SessionView ticket={ticket} onOpenTab={onTab} teamReporter={teamReporter} />
          </ModalBody>
        ))}
    </Modal>
  );
}
